/* eslint-disable @typescript-eslint/no-non-null-assertion */
import type { GetCustomFieldsAcrossAllSpacesBffResponse, CustomFieldResource, CustomFieldValueResource } from "@octopusdeploy/octopus-server-client";
import { repository } from "~/clientInstance";
const customFieldSeparator = "/";
const getAllTenantCustomFields = (): Promise<CustomFieldResource[]> => repository.CustomFields.getAllByScopes(["Tenant"]);
const getAllEnvironmentCustomFields = (): Promise<CustomFieldResource[]> => repository.CustomFields.getAllByScopes(["Environment"]);
const getAllCustomFieldsAcrossAllSpacesBff = (): Promise<GetCustomFieldsAcrossAllSpacesBffResponse> => repository.CustomFields.allCustomFieldsAcrossAllSpaces();
interface CustomFieldGroup {
    name: string;
    customFields: string[];
}
export interface CustomFieldGroupWithCustomFieldValueResource {
    id: string;
    name: string;
    values: CustomFieldValueResource[];
}
interface CustomFieldIndex {
    [canonicalFieldName: string]: CustomFieldValueResource;
}
const getTenantCustomFieldIndex = async (): Promise<CustomFieldIndex> => {
    const customFields = await getAllTenantCustomFields();
    return getCustomFieldIndexForCustomFields(customFields);
};
const getEnvironmentCustomFieldIndex = async (): Promise<CustomFieldIndex> => {
    const customFields = await getAllEnvironmentCustomFields();
    return getCustomFieldIndexForCustomFields(customFields);
};
const getCustomFieldIndexForCustomFields = (customFields: CustomFieldResource[]): CustomFieldIndex => {
    const idx: {
        [canonicalFieldName: string]: CustomFieldValueResource;
    } = {};
    customFields.forEach((f) => f.Values!.forEach((customField) => {
        if (customField.CanonicalTagName) {
            idx[customField.CanonicalTagName] = customField;
        }
        if (customField.Id) {
            idx[customField.Id] = customField;
        }
    }));
    return idx;
};
const groupByCustomField = (canonicalFieldNames: string[]): CustomFieldGroup[] => {
    const customFieldNameLookup: Map<string, string[]> = new Map();
    canonicalFieldNames.forEach((canonicalFieldName: string) => {
        const [customFieldName] = canonicalFieldName.split(customFieldSeparator);
        const customField = customFieldNameLookup.get(customFieldName);
        if (customField) {
            customField.push(canonicalFieldName);
        }
        else {
            customFieldNameLookup.set(customFieldName, [canonicalFieldName]);
        }
    });
    return Array.from(customFieldNameLookup, ([customFieldName, customFields]) => ({ name: customFieldName, customFields }));
};
const groupAndOrderByCustomField = (canonicalFieldNames: string[], customFields: CustomFieldResource[]): CustomFieldGroup[] => {
    const unorderCustomFields = groupByCustomField(canonicalFieldNames);
    return unorderCustomFields.sort((a, b) => customFields.find((x) => x.Name === a.name)!.SortOrder - customFields.find((x) => x.Name === b.name)!.SortOrder);
};
const groupAndOrderByCustomFieldAndValue = (canonicalFieldNames: string[], customFields: CustomFieldResource[]): CustomFieldGroup[] => {
    const customFieldMap = new Map(customFields.map((customField) => [customField.Name, customField]));
    const orderedCustomFields = groupAndOrderByCustomField(canonicalFieldNames, customFields);
    orderedCustomFields.forEach((customField, i) => {
        orderedCustomFields[i].customFields = customField.customFields.sort((a, b) => {
            const customFields = customFieldMap.get(customField.name)!.Values;
            return customFields!.find((x) => x.CanonicalTagName === a)!.SortOrder - customFields!.find((x) => x.CanonicalTagName === b)!.SortOrder;
        });
    });
    return orderedCustomFields;
};
const flattenGroup = (groups: CustomFieldGroup[]): string[] => groups.reduce<string[]>((a, group) => a.concat(group.customFields), []);
const groupByCustomFieldWithValue = (customFields: CustomFieldValueResource[]): CustomFieldGroupWithCustomFieldValueResource[] => {
    const unorderedCustomFields: Map<string, CustomFieldGroupWithCustomFieldValueResource> = new Map();
    customFields.forEach((customField: CustomFieldValueResource) => {
        const [id] = customField.Id.split(customFieldSeparator);
        const customFieldWithValues = unorderedCustomFields.get(id);
        if (customFieldWithValues) {
            customFieldWithValues.values.push(customField);
        }
        else {
            const [customFieldName] = customField.CanonicalTagName.split(customFieldSeparator);
            unorderedCustomFields.set(id, { id, name: customFieldName, values: [customField] });
        }
    });
    return Array.from(unorderedCustomFields, ([, customFieldGroupWithValue]) => customFieldGroupWithValue);
};
const groupByFieldSetWithCustomFieldResourceAndOrderByFieldSetAndCustomField = (customFieldValues: CustomFieldValueResource[], customFields: CustomFieldResource[]): CustomFieldGroupWithCustomFieldValueResource[] => {
    const customFieldMap = new Map(customFields.map((customField) => [customField.Id, customField]));
    const unorderedCustomFields = groupByCustomFieldWithValue(customFieldValues);
    const orderedCustomFields = unorderedCustomFields.sort((a, b) => customFields.find((x) => x.Name === a.name)!.SortOrder - customFields.find((x) => x.Name === b.name)!.SortOrder);
    orderedCustomFields.forEach((customField, i) => {
        orderedCustomFields[i].values = customField.values.sort((a, b) => {
            const customFields = customFieldMap.get(customField.id)!.Values;
            return customFields!.find((x) => x.CanonicalTagName === a.CanonicalTagName)!.SortOrder - customFields!.find((x) => x.CanonicalTagName === b.CanonicalTagName)!.SortOrder;
        });
    });
    return orderedCustomFields;
};
interface TenantCustomFieldsLookup {
    customFields: Map<string, CustomFieldResource>;
    values: Map<string, CustomFieldWithSet>;
}
interface CustomFieldWithSet {
    customField: CustomFieldValueResource;
    set: CustomFieldResource;
}
const loadTenantCustomFieldsLookup = async (): Promise<TenantCustomFieldsLookup> => {
    const customFields = await getAllTenantCustomFields();
    const customFieldLookup = customFields.reduce((lookup, fs) => lookup.set(fs.Id, fs), new Map());
    const customFieldValueLookup = customFields.flatMap((fs) => fs.Values!.map((customField) => ({ customField, set: fs }))).reduce((lookup, item) => lookup.set(item.customField.Id, item), new Map<string, CustomFieldWithSet>());
    return { customFields: customFieldLookup, values: customFieldValueLookup };
};
export { getAllTenantCustomFields, getAllEnvironmentCustomFields, getAllCustomFieldsAcrossAllSpacesBff, getTenantCustomFieldIndex, getEnvironmentCustomFieldIndex, getCustomFieldIndexForCustomFields, groupByCustomField, groupAndOrderByCustomField, groupAndOrderByCustomFieldAndValue, groupByCustomFieldWithValue, groupByFieldSetWithCustomFieldResourceAndOrderByFieldSetAndCustomField, flattenGroup, loadTenantCustomFieldsLookup, CustomFieldIndex, TenantCustomFieldsLookup, CustomFieldWithSet, };
